/**
 * PATH: apps/quote/src/components/builder/scope-ag-grid/bravada-overrides-band.ts
 * FILE_ID: QB-AGGRID-BRAVADA-OVERRIDES-001
 * PURPOSE: Adds a per-line OVERRIDES column band so estimators can
 *          break the global assumption defaults (material markup,
 *          wastage, plant markup, subcon markup, downtime) on
 *          individual scope lines.
 * WHY:    The backend QuoteLineItem already carries override_*
 *         fields (override_material_markup, override_material_wastage,
 *         override_plant_markup, override_subcon_markup,
 *         override_downtime). The legacy AG-Grid surfaced only the
 *         derived results — there was no way to TYPE an override on a
 *         specific line; estimators had to edit the underlying cost
 *         to fake one. This band exposes each override as a directly
 *         editable cell, persisted through the standard cell-edit
 *         persistence pipeline (useScopeAgPersistHandlers). When a
 *         cell is null the engine falls back to quote.config defaults
 *         on save — same behaviour as today.
 *
 * EVIDENCE:
 *   Backend model:    quotebuilder/models/line_item.py
 *     - override_material_markup    (DecimalField, null=True)
 *     - override_material_wastage   (DecimalField, null=True)
 *     - override_plant_markup       (DecimalField, null=True)
 *     - override_subcon_markup      (DecimalField, null=True)
 *     - override_downtime           (DecimalField, null=True)
 *   Backend serializer: quotebuilder/serializers/line_item.py L161-165
 *     fields all listed + 'allow_null: True'
 *   Backend resolution: line_item.py::recompute_totals
 *     resolves override → quote.config → org default in that order
 *
 * RELATED:
 *   - QB-AGGRID-BRAVADA-BANDS-001 (bravada-column-bands.ts) — assembles
 *     this band into BRAVADA_COLUMN_BANDS.
 *   - QB-AGGRID-FMT-001 (scope-ag-grid-formatters.ts) — reads the same
 *     override fields for the "computed" markup % cells (the values
 *     edited here flow through that path on next render).
 */

import type { ColumnBand } from '../scope-sheet-grid/ScopeSheetGridTypes'
import { numCol, WIDTH } from './bravada-band-helpers'

/**
 * Display semantics:
 *   - All cells store decimal fractions in the DB (0.10 = 10%).
 *   - The cell renderer in scope-ag-grid-formatters.ts already
 *     multiplies by 100 for display (see material_markup_percent
 *     resolver). New override columns follow the same convention.
 *   - When the cell is empty the underlying field is null → engine
 *     falls back to quote.config.{materialMarkup,materialWastage,
 *     plantMarkup,subcontractorMarkup,downtimeFactor}.
 */
export const BRAVADA_OVERRIDES_BAND: ColumnBand = {
  id: 'overrides',
  label: 'PER-LINE OVERRIDES',
  color: 'violet',
  bgClass: 'bg-violet-50/50 dark:bg-violet-950/30',
  textClass: 'text-violet-700 dark:text-violet-400',
  columns: [
    // Backend field: override_material_markup (decimal fraction).
    // Empty cell → quote.config.materialMarkup default applies.
    numCol({
      id: 'override_material_markup',
      label: 'Material Markup %',
      width: WIDTH.overridePercent,
      editable: true,
    }),
    // Backend field: override_material_wastage. Wastage compounds
    // on top of cost BEFORE markup, so leaving null vs 0.0 has
    // identical effect at save time; we accept either for now.
    numCol({
      id: 'override_material_wastage',
      label: 'Material Wastage %',
      width: WIDTH.overridePercent,
      editable: true,
    }),
    // Backend field: override_plant_markup. Plant markup is
    // typically lower than material markup (15-20% vs 25-30%);
    // separate override lets estimators encode that ratio per line.
    numCol({
      id: 'override_plant_markup',
      label: 'Plant Markup %',
      width: WIDTH.overridePercent,
      editable: true,
    }),
    // Backend field: override_subcon_markup. Subcon margins are
    // contract-negotiated and vary wildly by trade — per-line
    // override is more useful here than for materials.
    numCol({
      id: 'override_subcon_markup',
      label: 'Subcon Markup %',
      width: WIDTH.overridePercent,
      editable: true,
    }),
    // Backend field: override_downtime. Productivity buffer for
    // hard-access / high-risk lines (e.g. confined-space cells
    // need higher downtime than open-deck waterproofing).
    numCol({
      id: 'override_downtime',
      label: 'Downtime %',
      width: WIDTH.overridePercent,
      editable: true,
    }),
  ],
}
